import { useContext } from "react";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { fetchGraphQL } from "../../Graphql/utils";
import { API_RESPONSE_CODE } from "@kivunova/kivufrontendcommon";
import {
  addToStore,
  setStoreList,
  setStoreUsers
} from "../../store/slices/storeSlice";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";

import {
  addStoreSupplierMutation,
  assignStoreAdminMutation,
  changeStoreManagerQuery,
  createStoreMutation,
  deleteStoreMutation,
  getCompanyStoresQuery,
  getStoreDetailsQuery,
  listStoresUsersQuery,
  removeStoreSupplierMutation,
  updateStoreMutation,
  updateStoreStatusMutation
} from "../../Graphql/querries/storeQuery";

import {
  AddStoreSupplierRequest,
  AddStoreSupplierResponse,
  AssignStoreAdminRequest,
  AssignStoreAdminResponse,
  ChangeStoreManagerRequest,
  ChangeStoreManagerResponse,
  CompanyStore,
  CreateStoreRequest,
  CreateStoreResponse,
  DeleteStoreResponse,
  GetCompanyStoreRequest,
  GetCompanyStoreResponse,
  GetStoreDetailsRequest,
  GetStoreDetailsResponse,
  ListStoreUsersRequest,
  ListStoreUsersResponse,
  RemoveStoreSupplierRequest,
  RemoveStoreSupplierResponse,
  UpdateStoreRequest,
  UpdateStoreResponse,
  UpdateStoreStatusRequest,
  UpdateStoreStatusResponse
} from "../../components/Store/types/storeType";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

// 🏬 Get all stores of a company
export const useGetCompanyStores = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<GetCompanyStoreResponse, Error, GetCompanyStoreRequest>({
    mutationKey: ["lstCompanyStore"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching company stores",
        buildLogContext(
          "stores",
          "get-company-stores",
          // userId not available — this hook does not have access to userDetails
          undefined,
          input.companyId,
          // storeId not applicable for a company-wide store list
          undefined
        )
      );
      return fetchGraphQL(getCompanyStoresQuery, { input }, setAlert);
    },
    onSuccess: (response, input) => {
      const { code, stores } = response.listStores;

      if (code === API_RESPONSE_CODE.OK) {
        dispatch(setStoreList(stores));

        sendAppLog(
          LOG_LEVEL.INFO,
          "Company stores fetched successfully",
          buildLogContext(
            "stores",
            "get-company-stores",
            undefined, // userId not available in onSuccess scope
            input.companyId,
            undefined, // storeId not applicable for a company-wide store list
            { storeCount: stores?.length ?? 0 }
          )
        );
      } else {
        // ⚠️ Expected API/business failure
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Failed to fetch company stores",
          buildLogContext(
            "stores",
            "get-company-stores",
            undefined, // userId not available in onSuccess scope
            input.companyId,
            undefined, // storeId not applicable for a company-wide store list
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as unknown as ALERT_BANNER_CODE
        });
      }
    }
  });
};

/** Single store by id (GraphQL getStoreDetails). */
export const useGetStoreDetails = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<GetStoreDetailsResponse, Error, GetStoreDetailsRequest>({
    mutationKey: ["getStoreDetails"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching store details",
        buildLogContext(
          "stores",
          "get-store-details",
          // userId not available — this hook does not have access to userDetails
          undefined,
          undefined, // companyId not on GetStoreDetailsRequest
          input.storeId
        )
      );
      return fetchGraphQL(getStoreDetailsQuery, { input }, setAlert);
    }
  });
};

// ➕ Create a new store
export const useCreateStore = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<CreateStoreResponse, Error, CreateStoreRequest>({
    mutationKey: ["createStore"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating store",
        buildLogContext(
          "stores",
          "create-store",
          // userId not available — this hook does not have access to userDetails
          undefined,
          input.companyId,
          // storeId not yet known before creation
          undefined
        )
      );
      return fetchGraphQL(createStoreMutation, { input }, setAlert);
    },
    onSuccess: (response, input) => {
      const { code, store } = response.createStore;

      if (code === API_RESPONSE_CODE.OK && store) {
        dispatch(addToStore(store as CompanyStore));

        sendAppLog(
          LOG_LEVEL.INFO,
          "Store created successfully",
          buildLogContext(
            "stores",
            "create-store",
            undefined, // userId not available in onSuccess scope
            input.companyId,
            store.id // storeId is now known from the response
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.STORE_CREATED
        });
      } else {
        // ⚠️ Expected API/business failure
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Store creation rejected by API",
          buildLogContext(
            "stores",
            "create-store",
            undefined, // userId not available in onSuccess scope
            input.companyId,
            undefined, // storeId not available — creation failed
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as unknown as ALERT_BANNER_CODE
        });
      }
    }
  });
};

// ✏️ Update store details
export const useUpdateStore = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<UpdateStoreResponse, Error, UpdateStoreRequest>({
    mutationKey: ["updateStore"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating store",
        buildLogContext(
          "stores",
          "update-store",
          // userId not available — this hook does not have access to userDetails
          undefined,
          undefined, // companyId not on UpdateStoreRequest
          input.storeId
        )
      );
      return fetchGraphQL(updateStoreMutation, { input }, setAlert);
    },
    onSuccess: (response, input) => {
      const { code } = response.updateStore;

      if (code !== API_RESPONSE_CODE.OK) {
        // ⚠️ Expected API/business failure
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Store update rejected by API",
          buildLogContext(
            "stores",
            "update-store",
            undefined, // userId not available in onSuccess scope
            undefined, // companyId not on UpdateStoreRequest
            input.storeId,
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as unknown as ALERT_BANNER_CODE
        });
        throw new Error(code);
      } else {
        sendAppLog(
          LOG_LEVEL.INFO,
          "Store updated successfully",
          buildLogContext(
            "stores",
            "update-store",
            undefined, // userId not available in onSuccess scope
            undefined, // companyId not on UpdateStoreRequest
            input.storeId
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: code as unknown as ALERT_BANNER_CODE
        });
      }
    }
  });
};

// 🔄 Update store status
export const useUpdateStoreStatus = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<
    UpdateStoreStatusResponse,
    Error,
    UpdateStoreStatusRequest
  >({
    mutationKey: ["updateStoreStatus"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating store status",
        buildLogContext(
          "stores",
          "update-store-status",
          // userId not available — this hook does not have access to userDetails
          undefined,
          undefined, // companyId not on UpdateStoreStatusRequest
          input.storeId
        )
      );
      return fetchGraphQL(updateStoreStatusMutation, { input }, setAlert);
    },
    onSuccess: (response, input) => {
      const { code } = response.updateStoreStatus;

      if (code !== API_RESPONSE_CODE.OK) {
        // ⚠️ Expected API/business failure
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Store status update rejected by API",
          buildLogContext(
            "stores",
            "update-store-status",
            undefined, // userId not available in onSuccess scope
            undefined, // companyId not on UpdateStoreStatusRequest
            input.storeId,
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as unknown as ALERT_BANNER_CODE
        });
        throw new Error(code);
      }
    },
    onError: (error, input) => {
      // ❌ Unexpected runtime/network error
      captureAppException(
        "Failed to update store status",
        error,
        buildLogContext(
          "stores",
          "update-store-status",
          undefined, // userId not available in onError scope
          undefined, // companyId not on UpdateStoreStatusRequest
          input?.storeId
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: error.message
          ? (error.message as ALERT_BANNER_CODE)
          : ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

// 👤 Assign store admin
export const useAssignStoreAdmin = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<AssignStoreAdminResponse, Error, AssignStoreAdminRequest>({
    mutationKey: ["assignStoreAdmin"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started assigning store admin",
        buildLogContext(
          "stores",
          "assign-store-admin",
          // userId not available — this hook does not have access to userDetails
          undefined,
          undefined, // companyId not on AssignStoreAdminRequest
          input.storeId
        )
      );
      return fetchGraphQL(assignStoreAdminMutation, { input }, setAlert);
    },
    onSuccess: (response, input) => {
      const { code } = response.assignStoreAdmin;

      if (code !== API_RESPONSE_CODE.OK) {
        // ⚠️ Expected API/business failure
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Assign store admin rejected by API",
          buildLogContext(
            "stores",
            "assign-store-admin",
            undefined, // userId not available in onSuccess scope
            undefined, // companyId not on AssignStoreAdminRequest
            input.storeId,
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as unknown as ALERT_BANNER_CODE
        });
        throw new Error(code);
      }
    }
  });
};

// ➕ Add store supplier
export const useAddStoreSupplier = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<AddStoreSupplierResponse, Error, AddStoreSupplierRequest>({
    mutationKey: ["addStoreSupplier"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started adding supplier to store",
        buildLogContext(
          "stores",
          "add-store-supplier",
          // userId not available — this hook does not have access to userDetails
          undefined,
          undefined, // companyId not on AddStoreSupplierRequest
          input.storeId,
          { supplierId: String(input.supplierId) }
        )
      );
      return fetchGraphQL(addStoreSupplierMutation, { input }, setAlert);
    },
    onSuccess: (response, input) => {
      const { id } = response.addStoreSupplier;

      if (!id) {
        // ❌ Logically broken success response
        captureAppException(
          "Add store supplier failed: missing id in response",
          new Error("Add store supplier failed: missing id in response"),
          buildLogContext(
            "stores",
            "add-store-supplier",
            undefined, // userId not available in onSuccess scope
            undefined, // companyId not on AddStoreSupplierRequest
            input.storeId,
            { supplierId: String(input.supplierId) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new Error("Failed to add supplier");
      }

      sendAppLog(
        LOG_LEVEL.INFO,
        "Supplier added to store successfully",
        buildLogContext(
          "stores",
          "add-store-supplier",
          undefined, // userId not available in onSuccess scope
          undefined, // companyId not on AddStoreSupplierRequest
          input.storeId,
          { supplierId: String(input.supplierId) }
        )
      );
    }
  });
};

// ❌ Remove store supplier
export const useRemoveStoreSupplier = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<
    RemoveStoreSupplierResponse,
    Error,
    RemoveStoreSupplierRequest
  >({
    mutationKey: ["removeStoreSupplier"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started removing supplier from store",
        buildLogContext(
          "stores",
          "remove-store-supplier",
          // userId not available — this hook does not have access to userDetails
          undefined,
          undefined, // companyId not on RemoveStoreSupplierRequest
          input.storeId,
          { supplierId: String(input.supplierId) }
        )
      );
      return fetchGraphQL(removeStoreSupplierMutation, { input }, setAlert);
    },
    onSuccess: (response, input) => {
      const { id } = response.removeStoreSupplier;

      if (!id) {
        // ❌ Logically broken success response
        captureAppException(
          "Remove store supplier failed: missing id in response",
          new Error("Remove store supplier failed: missing id in response"),
          buildLogContext(
            "stores",
            "remove-store-supplier",
            undefined, // userId not available in onSuccess scope
            undefined, // companyId not on RemoveStoreSupplierRequest
            input.storeId,
            { supplierId: String(input.supplierId) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new Error("Failed to remove supplier");
      }

      sendAppLog(
        LOG_LEVEL.INFO,
        "Supplier removed from store successfully",
        buildLogContext(
          "stores",
          "remove-store-supplier",
          undefined, // userId not available in onSuccess scope
          undefined, // companyId not on RemoveStoreSupplierRequest
          input.storeId,
          { supplierId: String(input.supplierId) }
        )
      );
    }
  });
};

// 👥 List users in a store
/**
 * useListStoreUsers
 * Fetches and stores list of users for a store
 */
export const useListStoreUsers = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<ListStoreUsersResponse, Error, ListStoreUsersRequest>({
    mutationKey: ["listStoresUsers"],

    mutationFn: async (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching store users",
        buildLogContext(
          "stores",
          "list-store-users",
          // userId not available — this hook does not have access to userDetails
          undefined,
          undefined, // companyId not on ListStoreUsersRequest
          // storeIds is an array; log the first as the primary context identifier
          input.storeIds?.[0]
        )
      );

      return fetchGraphQL<
        ListStoreUsersResponse,
        { input: ListStoreUsersRequest }
      >(listStoresUsersQuery, { input }, setAlert);
    },

    onSuccess: (response, input) => {
      const result = response?.listStoresUsers;
      const { code, users } = result || {};

      if (code !== API_RESPONSE_CODE.OK || !users || !result) {
        // ⚠️ Expected API/business failure
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Failed to fetch store users",
          buildLogContext(
            "stores",
            "list-store-users",
            undefined, // userId not available in onSuccess scope
            undefined, // companyId not on ListStoreUsersRequest
            input.storeIds?.[0],
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      dispatch(setStoreUsers(users));
    },

    onError: (error, input) => {
      // ❌ Unexpected runtime/network error
      captureAppException(
        "Failed to fetch store users",
        error,
        buildLogContext(
          "stores",
          "list-store-users",
          undefined, // userId not available in onError scope
          undefined, // companyId not on ListStoreUsersRequest
          input.storeIds?.[0]
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useDeleteStore
 * Delete existing store
 */
export const useDeleteStore = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<DeleteStoreResponse, Error, string>({
    mutationKey: ["deleteStore"],
    mutationFn: (storeId) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started deleting store",
        buildLogContext(
          "stores",
          "delete-store",
          // userId not available — this hook does not have access to userDetails
          undefined,
          undefined, // companyId not available as a standalone arg
          storeId
        )
      );
      return fetchGraphQL(
        deleteStoreMutation,
        { input: { storeId } },
        setAlert
      );
    }
  });
};

export const useChangeStoreManager = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<
    ChangeStoreManagerResponse,
    Error,
    ChangeStoreManagerRequest
  >({
    mutationKey: ["changeStoreManager"],
    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started changing store manager",
        buildLogContext(
          "stores",
          "change-store-manager",
          // userId not available — this hook does not have access to userDetails
          undefined,
          undefined, // companyId not on ChangeStoreManagerRequest
          input.storeId
        )
      );
      return fetchGraphQL(changeStoreManagerQuery, { input }, setAlert);
    }
  });
};

// ─── Order module store listing (merged from OrderManagementMFE) ───────────────

export interface StoreAddress {
  country: string | null;
  province: string | null;
  district: string | null;
  sector: string | null;
  cell: string | null;
  village: string | null;
  streetAddress: string | null;
}

export interface Store {
  id: string;
  companyId: string;
  name: string;
  managerId: string | null;
  status: string;
  contactEmail: string | null;
  contactPhone: string | null;
  address: StoreAddress | null;
}

interface ListStoresInput {
  companyId: string;
}

interface ListStoresApiResponse {
  listStores: {
    code: string;
    stores: Store[];
  };
}

export const useListStores = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<Store[], Error, ListStoresInput>({
    mutationKey: ["listStores"],
    mutationFn: async (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching store list",
        buildLogContext(
          "stores",
          "list-stores",
          undefined,
          input.companyId,
          undefined
        )
      );

      try {
        const response = (await fetchGraphQL(
          getCompanyStoresQuery,
          { input: { companyId: input.companyId } },
          setAlert
        )) as ListStoresApiResponse;

        if (!response?.listStores) {
          captureAppException(
            "Unexpected response shape — listStores missing",
            new Error("Unexpected response shape — listStores missing"),
            buildLogContext(
              "stores",
              "list-stores",
              undefined,
              input.companyId,
              undefined
            )
          );
          throw new Error("Unexpected response shape — listStores missing");
        }

        const { code, stores } = response.listStores;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch store list",
            buildLogContext(
              "stores",
              "list-stores",
              undefined,
              input.companyId,
              undefined,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        return stores ?? [];
      } catch (error) {
        captureAppException(
          "Failed to fetch store list",
          error,
          buildLogContext(
            "stores",
            "list-stores",
            undefined,
            input.companyId,
            undefined
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    },
    onSuccess: (stores, input) => {
      dispatch(setStoreList(stores as unknown as CompanyStore[]));
      sendAppLog(
        LOG_LEVEL.INFO,
        "Store list fetched and dispatched",
        buildLogContext(
          "stores",
          "list-stores",
          undefined,
          input.companyId,
          undefined,
          {
            storeCount: stores.length
          }
        )
      );
    }
  });
};
